import React, { useEffect, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import fuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import fleetIcon from "../../../../Assets/Icons/laptop_car.svg";
import activeVehicleIcon from "../../../../Assets/Icons/delivery_truck_bolt.svg";
import vendorIcon from "../../../../Assets/Icons/storefront.svg";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import LineChart from "../../../../Components/Charts/LineChart";
import StackedBarChart from "../../../../Components/Charts/StackedBarChart";
import PieChart from "../../../../Components/Charts/PieChart";
import GenericTable from "../../../../Components/Tables/GenericTable";
import { useNavigate } from "react-router-dom";
import { useDateFilter } from "../../../../Contexts/DateFilterContext";
import { getAdminReports } from "../../../../API/Admin/Reports";

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const Overview = () => {
  const navigate = useNavigate();
  const { from, to } = useDateFilter();
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const startDate = from ? from.split(" ")[0] : undefined;
        const endDate = to ? to.split(" ")[0] : undefined;

        const res = await getAdminReports(undefined, startDate, endDate);

        if (Math.floor(res?.status / 100) === 2) {
          setReportData(res?.data?.data || null);
        }
      } catch (err) {
        console.error("Failed to load overview report:", err);
      }
    };

    fetchReport();
  }, [from, to]);

  const stats = reportData?.stats || {};
  const fuelSpendTrend = reportData?.fuel_spend_trend;
  const topFleets = reportData?.top_fleets;
  const topCustomers = reportData?.top_customers;
  const revenueByModule = reportData?.revenue_by_module || [];

  const handleView = (record) => {
    navigate("/vendor-detail", { state: { id: record.key } });
  };

  const vendorData = (reportData?.vendor_performance || []).map((vendor) => ({
    key: String(vendor.fuel_vendor_id),
    vendor: vendor.fuel_vendor_name || "-",
    type: Array.isArray(vendor.fuel_types_offered)
      ? vendor.fuel_types_offered
      : [],
    volume: vendor.total_volume ? `${vendor.total_volume} L` : "-",
    cost: formatCurrency(vendor.total_amount),
    status: capitalize(vendor.status) || "-",
  }));

  const uniqueTypes = [...new Set(vendorData.flatMap((item) => item.type))];

  const typeFilters = uniqueTypes.map((t) => ({
    text: t,
    value: t,
  }));

  const statusFilters = [...new Set(vendorData.map((item) => item.status))].map(
    (status) => ({ text: status, value: status }),
  );

  const vendorColumns = [
    {
      title: "Vendors",
      dataIndex: "vendor",
      key: "vendor",
      render: (text) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span>{text}</span>
        </div>
      ),
      enableSearch: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (types) => (
        <div
          style={{
            display: "flex",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          {types.length > 0
            ? types.map((t) => (
                <span key={t} className="type-badge">
                  {t}
                </span>
              ))
            : "-"}
        </div>
      ),

      filters: typeFilters,

      onFilter: (value, record) => record.type.includes(value),
    },
    {
      title: "Total Fuel Volume",
      dataIndex: "volume",
      key: "volume",
      sorter: (a, b) =>
        (parseFloat(a.volume) || 0) - (parseFloat(b.volume) || 0),
    },
    {
      title: "Total Fuel Cost",
      dataIndex: "cost",
      key: "cost",
      sorter: (a, b) =>
        (parseFloat(a.cost.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.cost.replace(/[$,]/g, "")) || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-tag ${status.toLowerCase()}`}>{status}</span>
      ),
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <span
              onClick={() => handleView(record)}
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                color: "#4F46E5",
              }}
            >
              <EyeOutlined style={{ fontSize: 18 }} />
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="overview">
      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Total Fuel Spend"
            value={formatCurrency(
              stats.total_fuel_spend?.value ?? stats.total_fuel_spend,
            )}
            subtitle="vs. last month"
            icon={fuelIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Active Fleet"
            value={
              stats.total_active_fleets?.value ?? stats.total_active_fleets ?? 0
            }
            subtitle="vs. last month"
            icon={fleetIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Active Vehicles"
            value={
              stats.total_active_vehicles?.value ??
              stats.total_active_vehicles ??
              0
            }
            subtitle="vs. last month"
            icon={activeVehicleIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Vendors"
            value={stats.total_vendors?.value ?? stats.total_vendors ?? 0}
            subtitle="vs. last month"
            icon={vendorIcon}
          />
        </div>
      </div>

      <div className="row">
        <div className="graph">
          <LineChart
            graphData={{
              labels: fuelSpendTrend?.labels || [],
              Spend: (fuelSpendTrend?.spend || []).map(
                (v) => parseFloat(v) || 0,
              ),
              Volume: (fuelSpendTrend?.volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={2}
            colourTheme={["#F59E0B", "#4F46E5"]}
            graphTitle="Fuel Spend"
            selectTenByDefault={false}
            units={["$", "L"]}
            classNameCSS="fuel-spend"
          />
        </div>
        <div className="graph">
          <StackedBarChart
            graphData={{
              labels: topFleets?.labels || [],
              Volume: (topFleets?.total_volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
              Amount: (topFleets?.total_amount || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={2}
            colourTheme={["#4F46E5", "#EA580C"]}
            graphTitle="Top Fleets"
            selectTenByDefault={false}
            units={["L", "$"]}
            classNameCSS="top-fleets"
          />
        </div>
      </div>

      <div className="row">
        <div className="graph">
          <PieChart
            graphData={{
              labels: revenueByModule.map((m) => m.module_name || "-"),
              Revenue: revenueByModule.map(
                (m) => parseFloat(m.total_amount) || 0,
              ),
            }}
            graphTitle="Revenue By Module"
            unit="$"
            classNameCSS="revenue-by-module"
          />
        </div>
        <div className="graph ">
          <StackedBarChart
            graphData={{
              labels: topCustomers?.labels || [],
              Volume: (topCustomers?.total_volume || []).map(
                (v) => parseFloat(v) || 0,
              ),
              Amount: (topCustomers?.total_amount || []).map(
                (v) => parseFloat(v) || 0,
              ),
            }}
            lengthKeys={2}
            colourTheme={["#16A34A", "#4F46E5"]}
            graphTitle="Top Customers"
            selectTenByDefault={false}
            units={["L", "$"]}
            classNameCSS="top-customers"
          />
        </div>
      </div>
      <div className="vendor-table">
        <GenericTable
          title="Vendor Performance"
          dataSource={vendorData}
          columns={vendorColumns}
        />
      </div>
    </div>
  );
};

export default Overview;
